import { debugError } from '@utils';

class ProductLookUp {
  static #version = '2025-07';
  #cache = new Map();

  /**
   * The metaobject entry selection shared by the `add_on` metafields. It was spelled out
   * four times before NGE-13453 — twice per query, two queries — which is how the two
   * copies were free to drift without anything noticing.
   * @returns {string}
   */
  static #entryFields = () => `
    entries: references(first: 100) {
      nodes {
        ... on Metaobject {
          id
          handle
          fields {
            key
            type
            value
            images: references(first: 10) {
              nodes {
                ... on MediaImage {
                  id
                  image {
                    url
                    altText
                    width
                    height
                  }
                }
              }
            }
          }
        }
      }
    }
  `;

  /**
   * The variant selection every lookup needs, written once.
   * @param {string} scope — window.theme.app_scope, prefixes the tier metafield namespace
   * @returns {string}
   */
  static #variantFields = (scope) => `
    id
    sku
    title
    availableForSale
    price {
      amount
      currencyCode
    }
    compareAtPrice {
      amount
      currencyCode
    }
    addOns: metafield(namespace: "add_on", key: "products") {
      key
      type
      ${ProductLookUp.#entryFields()}
    }
    options: metafield(namespace: "add_on", key: "options") {
      key
      type
      ${ProductLookUp.#entryFields()}
    }
    original_price: metafield(namespace: "${scope}cm_tier", key: "strike_price") {
      value
      key
      type
    }
  `;

  /**
   * PRIMARY query — one `product(handle:)` per candidate handle, aliased so any number of
   * candidates costs exactly one round trip.
   *
   * `product(handle:)` is a direct fetch: it resolves a product whatever its status, which
   * is the whole point of NGE-13453. Measured 2026-09-24 on stampa-stage against a product
   * genuinely set to UNLISTED (`portachiavi-con-foto`, `KRH0050051`): this returns it with
   * `availableForSale: true`, while `predictiveSearch` returns nothing for the same SKU.
   *
   * Several handles per print material is the NORMAL case, not an edge one: a PM is not
   * unique. On stit-prod `ACS` carries both `foto-su-acrilico` (`ACS0200201`) and
   * `sottobicchieri-personalizzati` (`ACSRND0909`), and `BED` and `BLC` collide the same
   * way. Only the full SKU disambiguates, which is why the caller matches on `v.sku`.
   *
   * @param {string[]} handles
   * @param {string} scope
   * @returns {string}
   */
  static #queryByHandles = (handles, scope) => `
    {
      ${handles
        .map(
          (handle, index) => `
      p${index}: product(handle: ${JSON.stringify(handle)}) {
        handle
        id
        title
        variants(first: 100, sortKey: POSITION) {
          nodes {
            ${ProductLookUp.#variantFields(scope)}
          }
        }
      }`
        )
        .join('\n')}
    }
  `;

  /**
   * FALLBACK query — predictiveSearch, which reads the storefront search index.
   * @param {string} sku
   * @param {string} scope
   * @returns {string}
   */
  static #queryProduct = (sku, scope) => `
    {
      predictiveSearch(query: ${JSON.stringify(sku)}, searchableFields: [VARIANTS_SKU], limit: 10) {
        products {
          handle
          id
          title
          variants(first: 100, sortKey: POSITION) {
            nodes {
              ${ProductLookUp.#variantFields(scope)}
            }
          }
        }
      }
    }
  `;

  /**
   * Candidate product handles for a print material, from the Liquid-rendered index.
   *
   * The index is generated from the ADMIN API (`scripts/build-configurator-sku-index.mjs`)
   * and rendered by `html-bottom-js.liquid` out of a shop metafield. It is deliberately not
   * built by iterating products in Liquid: that caps out around 50 products — stampa-stage
   * already carries 57 and stit-prod 51 — and no storefront-side enumeration can see an
   * UNLISTED product at all (measured 2026-09-24: `products(first:250)` returned 35 and
   * omitted the UNLISTED one).
   *
   * @param {string} printMaterialSku
   * @returns {string[]}
   */
  static #handlesFor(printMaterialSku) {
    const index = window.theme?.configurator_pm_index;
    if (!index || !printMaterialSku) return [];

    // The PM reaches us from the React widget, where casing is not guaranteed — the add-on
    // strike map had the same problem and lost its match on case alone (NGE-11736).
    //
    // `hasOwnProperty` rather than a bare property access: the index is JSON from a shop
    // metafield, so a PM spelled `constructor` or `toString` would otherwise resolve to
    // something off Object.prototype and travel into the query builder as a non-string.
    const own = (key) => Object.prototype.hasOwnProperty.call(index, key);
    const key = own(printMaterialSku) ? printMaterialSku : String(printMaterialSku).toUpperCase();
    if (!own(key)) return [];

    const entry = index[key];
    // Strings only, for the same reason: the metafield is authored data and a hand-edited
    // entry must degrade to the search fallback, never to a malformed query.
    return (Array.isArray(entry) ? entry : [entry]).filter((h) => typeof h === 'string' && h);
  }

  /**
   * Find a variant by format and print material
   * @param {string} printMaterialSku (PM)
   * @param {string} formatSku (F)
   * @returns {string}
   */
  async find(printMaterialSku, formatSku) {
    if (!formatSku) return null;
    if (!printMaterialSku) return null;
    if (!window.theme.storefront_api_token) return null;

    // Format the variant SKU
    const sku = `${printMaterialSku}${formatSku}`;

    // Return cached result if available
    if (this.#cache.has(sku)) {
      return this.#cache.get(sku);
    }

    // Primary: the rendered PM index → a direct product(handle:) fetch. No search index.
    const handles = ProductLookUp.#handlesFor(printMaterialSku);
    if (handles.length) {
      try {
        const data = await this.#findByHandles(sku, handles);
        if (data) {
          this.#cache.set(sku, data);
          return data;
        }
      } catch (error) {
        debugError('Handle lookup failed, trying search fallback', error);
      }
    }

    // Fallback: predictiveSearch, reached only when the index does not know this PM.
    //
    // It covers a LISTED product, and only a listed one. An earlier revision of this comment
    // claimed that was the only case a miss could arise in — it is not (@YashitTCG, PR #297).
    // A product created, published from draft, set straight from archived to active/unlisted,
    // re-handled or given a new PM (incl. a changed SKU prefix where there is no sku.pm) AFTER the
    // last index run is missing from the index. A LISTED one is still found here; an UNLISTED one
    // is invisible to the search index too, so it lands back on the original bug until the index
    // is regenerated. The index refreshes on every release (deploy.mjs buildChain, release path);
    // between releases such a product needs `npm run build:sku-index -- --shop=<code> --write`.
    try {
      const data = await this.#findByPredictiveSearch(sku);
      if (data) {
        this.#cache.set(sku, data);
        return data;
      }
    } catch (error) {
      debugError('predictiveSearch fallback also failed', error);
    }

    throw new Error(window.toast_translations.cart.no_product);
  }

  /**
   * Primary lookup — direct fetch by handle, independent of the search index.
   * @param {string} sku
   * @param {string[]} handles
   * @returns {Object|null}
   */
  async #findByHandles(sku, handles) {
    const query = ProductLookUp.#queryByHandles(handles, window.theme.app_scope);

    const response = await fetch(`/api/${ProductLookUp.#version}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.theme.storefront_api_token,
      },
      body: JSON.stringify({ query }),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch product data (by handle)');
    }
    const responseData = await response.json();

    // Exact SKU match decides, never the alias order: two products can share a PM.
    for (const product of Object.values(responseData.data || {})) {
      const variant = product?.variants?.nodes?.find((v) => v.sku === sku);
      if (!variant) continue;

      const data = this.#formatData(variant, product);
      if (data?.id && data?.product?.handle) {
        return data;
      }
    }

    return null;
  }

  /**
   * Fallback lookup via predictiveSearch. DEPENDS ON THE SEARCH INDEX — an UNLISTED or
   * DRAFT product is not in it and cannot be found this way.
   *
   * This used to be the primary path, with a second `products(first: 5, query:
   * "variants.sku:<sku>")` behind it labelled "doesn't depend on search index". It did.
   * That query is a full-text search, not a SKU filter: measured against production it
   * returned `foto-poster`, `foto-su-acrilico` and `foto-incorniciata-con-tappetino` for
   * `ALP0200201`, none of which carries that SKU. It only ever failed safe because the
   * caller filtered on `v.sku` afterwards, and it could never succeed where this one
   * failed, since both read the same index. Removed in NGE-13453 rather than re-commented.
   *
   * @param {string} sku
   * @returns {Object|null}
   */
  async #findByPredictiveSearch(sku) {
    const query = ProductLookUp.#queryProduct(sku, window.theme.app_scope);

    const response = await fetch(`/api/${ProductLookUp.#version}/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': window.theme.storefront_api_token,
      },
      body: JSON.stringify({ query }),
    });
    if (!response.ok) {
      throw new Error('Failed to fetch product data');
    }
    const responseData = await response.json();

    const product = responseData.data.predictiveSearch.products?.[0];
    const variant = product?.variants?.nodes?.find((v) => v.sku === sku);
    const data = this.#formatData(variant, product);

    if (!data || !data?.id || !data?.product?.handle) {
      return null;
    }

    return data;
  }

  /**
   * Map the graphql response to a more readable format
   * @param {Object} variant
   * @param {Object} product
   * @returns {Object}
   */
  #formatData(variant, product) {
    if (!variant || !product) return null;

    // Format the data
    const data = {
      id: variant?.id,
      sku: variant?.sku,
      title: variant?.title,
      price: variant?.price,
      availableForSale: variant?.availableForSale,
      compareAtPrice: variant?.compareAtPrice,
      addOns: this.#formatAddOns(variant?.addOns?.entries?.nodes),
      options: this.#formatOptions(variant?.options?.entries?.nodes),
      original_price: JSON.parse(variant?.original_price?.value || '{}'),
      product: {
        id: product?.id,
        handle: product?.handle,
        title: product?.title,
      },
    };

    return data;
  }

  /**
   * Format the add-ons
   * @param {Object} addOns
   * @returns {Object}
   */
  #formatAddOns(addOns) {
    if (!addOns) return null;

    // Format the add-ons
    return addOns?.map((addOn) => {
      // Format the add-on metaobject
      const addOnData = {
        id: addOn?.id,
        handle: addOn?.handle,
        title: addOn?.title,
      };

      // Format the add-on fields
      addOn?.fields?.forEach((field) => {
        if (field?.type === 'list.file_reference' && field?.images?.nodes?.length > 0) {
          addOnData[field?.key] = this.#formatImages(field?.images?.nodes);
        } else {
          addOnData[field?.key] = field?.value;
        }
      });

      // Return the add-on data
      return addOnData;
    });
  }

  /**
   * Format the options
   * @param {Object} options
   * @returns {Object}
   */
  #formatOptions(options) {
    if (!options) return null;

    // Format the options
    return options?.map((option) => {
      // Format the option metaobject
      const optionData = {
        id: option?.id,
        handle: option?.handle,
        title: option?.title,
      };

      // Format the option fields
      option?.fields?.forEach((field) => {
        if (field?.type === 'list.file_reference' && field?.images?.nodes?.length > 0) {
          optionData[field?.key] = this.#formatImages(field?.images?.nodes);
        } else {
          optionData[field?.key] = field?.value;
        }
      });

      // Return the option data
      return optionData;
    });
  }

  /**
   * Format the images
   * @param {Object} images
   * @returns {Object}
   */
  #formatImages(images) {
    if (!images) return null;

    // Format the images
    return images?.map((data) => ({
      id: data?.id,
      url: data?.image?.url,
      altText: data?.image?.altText,
      width: data?.image?.width,
      height: data?.image?.height,
    }));
  }
}

const productLookUp = new ProductLookUp();
export { productLookUp };
